import type { CountryCode, E164Number } from "libphonenumber-js"

import { AsYouType } from "libphonenumber-js"

export type ParsedPhoneNumber =
  | {
      detectedCountry?: CountryCode
      isValid: false
      phoneNumber?: undefined
    }
  | {
      detectedCountry?: CountryCode
      isValid: true
      phoneNumber: {
        country: CountryCode
        countryCallingCode: string
        internationalPreview: string
        nationalFormat: string
        number: E164Number
      }
    }

/**
 * Takes a string and attempts to parse into a valid international phone number.
 *
 * When `defaultCountry` is provided the input is parsed as a national number
 * for that country, unless it starts with "+" in which case it is parsed as a
 * full international number (overriding the default country). Without
 * `defaultCountry` the legacy behavior applies: a "+" is prepended if missing
 * and the whole input is treated as international.
 * @param numberInput
 * @param defaultCountry
 */
export function parsePhoneNumberInput(
  numberInput: string | null,
  defaultCountry?: CountryCode,
): ParsedPhoneNumber {
  if (numberInput !== null) {
    let value = numberInput
    if (typeof defaultCountry === "undefined" && !value.startsWith("+")) {
      value = `+${value}`
    }

    const asYouType = new AsYouType(defaultCountry)
    asYouType.input(value)

    const parserResult = asYouType.getNumber()
    const detectedCountry = parserResult?.country ?? asYouType.getCountry()

    if (asYouType.isValid() && parserResult?.country) {
      const nationalFormat = parserResult.formatNational()
      return {
        detectedCountry,
        isValid: true,
        phoneNumber: {
          country: parserResult.country,
          countryCallingCode: parserResult.countryCallingCode,
          internationalPreview: `+${parserResult.countryCallingCode} ${nationalFormat}`,
          nationalFormat,
          number: parserResult.number,
        },
      }
    }
    return { detectedCountry, isValid: false }
  }
  return { isValid: false }
}
